/**
 * Recommend page (Week 2 — bug-fix revision).
 *
 *  • Top: collapsed AI textarea (natural-language → structured fields).
 *  • Centre: ONE row — Amount + Where (combo) + Find Best Card.
 *  • GLOBAL refinement: transaction-level context questions (portal,
 *    subcategory, etc.) shown ONCE for the whole transaction.
 *  • Bottom: ranked card list, each with cap annotation + calculator-style
 *    portion breakdown + per-card refinement (cycle-spend, milestone
 *    progress).
 *
 * Bug-fix changes from the first W2 cut:
 *  • Per-card overrides — Magnus's cycle-spend bucket NEVER pollutes Atlas.
 *  • Context questions are now GLOBAL — one set of chips per context_type,
 *    sourced from the union of values across all matching rules in the
 *    wallet (fixes the TravelOne `hsbc_travel_portal` invisibility bug).
 *  • Friendly chip labels via context_definitions registry.
 *  • Categories dropdown de-duplicated (case-insensitive) and pure-numeric
 *    MCC strings filtered out.
 *  • Merchants dropdown de-duplicated by slug.
 *  • Calculator view in each card row shows "₹X @ Y% rule A + ₹Z @ W% rule B".
 */
import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Loader2, Sparkles, IndianRupee, RotateCcw, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { Recommend, Merchants, Cards, api } from '@/lib/api';
import { computeWalletRefinement, aggregateAnswers } from '@/lib/refinement';
import { CardRow } from '@/components/recommend/CardRow';
import { AiIntentBox } from '@/components/recommend/AiIntentBox';
import { CategoryPicker } from '@/components/recommend/CategoryPicker';
import { GlobalRefinement } from '@/components/recommend/GlobalRefinement';
import { formatINR } from '@/lib/format';

const DEFAULT_QUERY = {
  amount: '',
  merchant: null,
  merchant_slug: null,
  // Phase-25.C: default to 'General' so the Recommend page is usable on
  // first load even before the user picks something. 'General' is seeded
  // as sort_order=1 in the canonical registry (see seed/categories_data.py).
  category: 'General',
  mode: 'online',
  international: false,
  include_milestones: true,
};

/** Drop unbalanced-paren / pure-numeric / numeric-prefixed strings that look
 * like polluted MCC values rather than real categories. Belt-and-braces for
 * the legacy data hygiene issue. */
const _looksLikeCategoryString = (s) => {
  const v = (s || '').toString().trim();
  if (!v) return false;
  if (/^\d/.test(v)) return false;                      // starts with a digit (MCC)
  const opens = (v.match(/\(/g) || []).length;
  const closes = (v.match(/\)/g) || []).length;
  if (opens !== closes) return false;                   // unbalanced parens
  return true;
};

export default function RecommendPage() {
  // Form state
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [globalAnswers, setGlobalAnswers] = useState({ contexts: {} });   // shared across cards
  const [answersByCard, setAnswersByCard] = useState({});                  // per-card, session-only
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [cards, setCards] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [registry, setRegistry] = useState({});                            // context_definitions
  const [walletLoading, setWalletLoading] = useState(true);

  // ── Load wallet + merchants + registry once ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [walletRes, merchRes, regRes] = await Promise.all([
          Cards.mine(),
          Merchants.list(),
          api.get('/api/contexts/registry?workspace=production').catch(() => ({ definitions: {} })),
        ]);
        if (cancelled) return;
        setCards(Array.isArray(walletRes) ? walletRes : []);
        // De-duplicate merchants by slug (workspace pollution may yield duplicates).
        const merchArr = Array.isArray(merchRes) ? merchRes : [];
        const bySlug = new Map();
        for (const m of merchArr) {
          if (m?.slug && !bySlug.has(m.slug)) bySlug.set(m.slug, m);
        }
        setMerchants(Array.from(bySlug.values()));
        setRegistry((regRes && regRes.definitions) || {});
      } catch (e) {
        console.error('wallet load failed', e);
        if (!cancelled) toast.error('Could not load your cards. Try again.');
      } finally {
        if (!cancelled) setWalletLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Categories list = union of `match.categories` across the user's wallet,
  // case-deduplicated, sanitized against MCC pollution.
  const walletCategories = useMemo(() => {
    const seen = new Map();   // lowercased → canonical (first-seen casing)
    for (const c of cards || []) {
      for (const r of (c.rules || [])) {
        for (const cat of (r?.match?.categories || [])) {
          if (!cat || !_looksLikeCategoryString(cat)) continue;
          const k = cat.trim().toLowerCase();
          if (!seen.has(k)) seen.set(k, cat.trim());
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [cards]);

  // Refinement schema, recomputed when query changes
  const { globalQuestions, perCard: schemaByCard, portalSubcategoryMap } = useMemo(() => {
    if (!cards.length) return { globalQuestions: [], perCard: {}, portalSubcategoryMap: {} };
    return computeWalletRefinement(
      cards,
      {
        amount: Number(query.amount) || 0,
        category: query.category,
        merchant_slug: query.merchant_slug,
        merchant: query.merchant,
        mcc: query.mcc,
      },
      registry,
    );
  }, [cards, registry, query.amount, query.category, query.merchant_slug, query.merchant, query.mcc]);

  // ── Run the engine ──────────────────────────────────────────────────────
  const runEngine = async () => {
    const amt = Number(query.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter an amount above ₹0.');
      return;
    }
    setRunning(true);
    try {
      const agg = aggregateAnswers(globalAnswers, answersByCard);
      const payload = {
        amount: amt,
        merchant: query.merchant || null,
        merchant_slug: query.merchant_slug || null,
        category: query.category || null,
        mode: query.mode || 'online',
        international: !!query.international,
        include_milestones: !!query.include_milestones,
        include_ai: false,
        contexts: agg.contexts,
        per_card_overrides: agg.per_card_overrides,
      };
      const res = await Recommend.run(payload);
      setResults(res);
    } catch (e) {
      console.error('recommend failed', e);
      toast.error('Recommendation engine error. Try again.');
    } finally {
      setRunning(false);
    }
  };

  // Whenever a refinement answer changes AFTER an initial run, re-run.
  useEffect(() => {
    if (!results) return;
    if (!query.amount) return;
    const t = setTimeout(() => { runEngine(); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answersByCard, globalAnswers]);

  const handleAiParsed = (parsed) => {
    setQuery((q) => ({
      ...q,
      amount: parsed.amount != null ? String(parsed.amount) : q.amount,
      merchant: parsed.merchant || q.merchant,
      merchant_slug: parsed.merchant_slug || q.merchant_slug,
      category: parsed.category || q.category,
      international: parsed.international != null ? parsed.international : q.international,
    }));
    // Apply parsed contexts to globalAnswers (one value per context_type).
    if (parsed.contexts && Object.keys(parsed.contexts).length) {
      const flat = {};
      for (const [ct, arr] of Object.entries(parsed.contexts)) {
        if (Array.isArray(arr) && arr.length) flat[ct] = arr[0];
      }
      if (Object.keys(flat).length) {
        setGlobalAnswers((prev) => ({ ...prev, contexts: { ...(prev.contexts || {}), ...flat } }));
      }
    }
  };

  const handleReset = () => {
    setQuery(DEFAULT_QUERY);
    setGlobalAnswers({ contexts: {} });
    setAnswersByCard({});
    setResults(null);
  };

  const ranked = results?.recommendation?.ranked || [];

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-5xl mx-auto" data-testid="recommend-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">
          Recommend
        </div>
        <h1 className="font-display text-3xl font-semibold text-foreground">
          Which card should I use?
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Tell us the amount and category. We&apos;ll rank your cards against the actual rules — including caps and milestones.
        </p>
      </div>

      <AiIntentBox onParsed={handleAiParsed} />

      {/* Structured form — one row */}
      <Card className="mt-3">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
            <div>
              <Label htmlFor="recommend-amount" className="text-xs text-muted-foreground mb-1.5 block">
                Amount
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="recommend-amount"
                  inputMode="decimal"
                  value={query.amount}
                  onChange={(e) => setQuery((q) => ({ ...q, amount: e.target.value.replace(/[^0-9.]/g, '') }))}
                  placeholder="e.g. 50000"
                  className="pl-9 h-11 tabular-nums"
                  data-testid="recommend-amount-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Category</Label>
              <CategoryPicker
                value={query.category}
                onChange={(category) => setQuery((q) => ({
                  ...q,
                  category,
                  // Clear merchant context whenever category changes — keeps
                  // refinement state coherent. RefinementPanel will re-ask
                  // any merchant-tier questions if needed.
                  merchant: null,
                  merchant_slug: null,
                  mcc: null,
                }))}
                walletCategories={walletCategories}
                testid="recommend-category-picker"
              />
            </div>
            <Button
              size="lg"
              className="h-11 px-6"
              onClick={runEngine}
              disabled={running || !query.amount}
              data-testid="recommend-find-button"
            >
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Find best card
            </Button>
          </div>

          <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={query.mode === 'online'}
                onCheckedChange={(v) => setQuery((q) => ({ ...q, mode: v ? 'online' : 'offline' }))}
                data-testid="recommend-mode-toggle"
              />
              <span className="text-muted-foreground">Online</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={!!query.international}
                onCheckedChange={(v) => setQuery((q) => ({ ...q, international: !!v }))}
                data-testid="recommend-international-toggle"
              />
              <span className="text-muted-foreground">International</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={!!query.include_milestones}
                onCheckedChange={(v) => setQuery((q) => ({ ...q, include_milestones: !!v }))}
                data-testid="recommend-milestones-toggle"
              />
              <span className="text-muted-foreground flex items-center gap-1">
                <Trophy className="h-3 w-3" /> Include milestone bonuses
              </span>
            </label>
            {(results || query.amount) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="ml-auto text-xs h-7 text-muted-foreground hover:text-foreground"
                data-testid="recommend-reset-button"
              >
                <RotateCcw className="h-3 w-3 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Global refinement (transaction-level contexts) */}
      {results && globalQuestions.length > 0 && (
        <div className="mt-3">
          <GlobalRefinement
            globalQuestions={globalQuestions}
            answers={globalAnswers}
            onChange={setGlobalAnswers}
            portalSubcategoryMap={portalSubcategoryMap}
          />
        </div>
      )}

      {/* Results */}
      <div className="mt-6 space-y-3">
        {walletLoading ? (
          <>
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </>
        ) : !results ? (
          <EmptyState hasCards={cards.length > 0} />
        ) : ranked.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              No cards matched. Try a different category or amount.
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-lg font-semibold text-foreground">
                Ranked for {formatINR(Number(query.amount) || 0)}
                {query.category && <span className="text-muted-foreground font-normal"> · {query.category}</span>}
                {query.merchant && <span className="text-muted-foreground font-normal"> · {query.merchant}</span>}
              </h2>
              {running && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            {ranked.map((rc, idx) => (
              <CardRow
                key={rc.card_id || idx}
                rank={idx + 1}
                ranked={rc}
                cardId={rc.card_id}
                refinementSchema={schemaByCard[rc.card_id] || { questions: [] }}
                answers={answersByCard[rc.card_id] || {}}
                onChangeAnswers={(cardId, next) => setAnswersByCard((prev) => ({ ...prev, [cardId]: next }))}
                isLoading={running}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const EmptyState = ({ hasCards }) => (
  <Card>
    <CardContent className="p-8 text-center">
      <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
        <Sparkles className="h-5 w-5" />
      </div>
      <h3 className="font-display text-lg font-semibold text-foreground mb-1">
        Ready when you are
      </h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        {hasCards
          ? 'Enter an amount and where you plan to spend. We\'ll rank your cards by realised reward — caps and milestones included.'
          : 'Add at least one card to your wallet, then come back to see ranked recommendations.'}
      </p>
    </CardContent>
  </Card>
);
